// analytics.service.ts
import { functions, httpsCallable } from '@util/firebase';
import { logError } from '@util/logError';

import { ProductDocument } from '@util/types/firestore/products';
import { MasterDocument } from '@util/firestore/master';
import { BlogDocument } from '@util/types/firestore/blog';

import { Item, EventTypes, PixelEvents } from './analytics.types';
import { CartItem } from '@util/types/firestore/carts';
import {
  omnisendTrackProductViewed,
  omnisendTrackAddedToCart,
  omnisendTrackStartedCheckout,
} from './omnisend';

export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID;
export const GTM_TRACKING_ID = process.env.NEXT_PUBLIC_GTM_TRACKING_ID;

// Meta Advanced Matching transport fix (post-#1409 ship-gate finding, GB
// #1373). `fbq('set', 'userData', {...})` is REJECTED by Meta's current
// pixel build — live-confirmed 2026-07-15, reproduced deterministically from
// a bare console call: "[Meta Pixel] - Call to \"fbq('set', 'userData',
// Object);\" with parameter \"pixel_id\" has an invalid value of
// \"'<id>'\"", sourced from fbevents.js:188. Identity never attached; the
// PR #1409 mechanism was inert-but-harmless (no revert needed — see the
// finding comment on PR #1409). Meta's documented Manual Advanced Matching
// transport is instead identity-AT-INIT: fbq('init', PIXEL_ID, userData)
// (https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching).
// That requires exposing the pixel id to app code.
//
// THREE-WAY SYNC SET (Codex P2 fix, GB #1373): this repo's env var docs
// (CLAUDE.md, docs/DATA-INTEGRATIONS.md) had already documented an analytics
// pixel id key — NEXT_PUBLIC_FACEBOOK_PIXEL_ID — but no code anywhere in the
// repo ever read it (grepped clean at the time of this fix; it was a
// documented-but-dormant placeholder, not a wired feature). Bill created
// NEXT_PUBLIC_META_PIXEL_ID fresh in Vercel for this PR before that mismatch
// was caught. Rather than pick one and silently orphan the other, this reads
// NEXT_PUBLIC_META_PIXEL_ID first and falls back to
// NEXT_PUBLIC_FACEBOOK_PIXEL_ID so whichever one ends up configured in
// Vercel works. All three of the following MUST stay in sync (same pixel
// id): NEXT_PUBLIC_META_PIXEL_ID, NEXT_PUBLIC_FACEBOOK_PIXEL_ID (fallback),
// and the GTM web container's `Meta || Pixel ID` constant (GTM-NGG9D35, tag
// 108 — the base pixel tag that fires the FIRST, identity-less init on every
// page load before any of our code runs).
export const META_PIXEL_ID =
  process.env.NEXT_PUBLIC_META_PIXEL_ID || process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

// Cosmetic fix (post-merge review, GB #1373): isPixelIdDrifted()'s console
// warning used to hardcode "NEXT_PUBLIC_META_PIXEL_ID" regardless of which
// env var actually supplied META_PIXEL_ID's value — misleading if the
// fallback (NEXT_PUBLIC_FACEBOOK_PIXEL_ID) is what's actually configured.
// This names whichever one really did.
const META_PIXEL_ID_SOURCE = process.env.NEXT_PUBLIC_META_PIXEL_ID
  ? 'NEXT_PUBLIC_META_PIXEL_ID'
  : 'NEXT_PUBLIC_FACEBOOK_PIXEL_ID';

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

// PR-0 (#1410): lets `trackEvent` reach window.dataLayer on a Vercel preview
// deploy so an event can be inspected there. It does NOT make previews
// "production-like" — GTM is only injected on production (_document.tsx), so
// a preview push still reaches no destination (GA4/Meta/Stape/TIE).
//
// `isPreview` is included (not just `analyticsDebug`) because
// NEXT_PUBLIC_VERCEL_ENV is set automatically by Vercel on every preview
// build with no dashboard action required; NEXT_PUBLIC_ANALYTICS_DEBUG is a
// manual opt-in kept for environments Vercel doesn't label "preview" (e.g.
// local dev).
//
// Scoped to `trackEvent` only: the other functions in this file gate real
// backend httpsCallable side effects (view/click/checkout counters) that
// have nothing to do with the GTM-absent argument and must not fire from
// every preview deploy. Purchase stays hard-blocked in `trackEvent`
// regardless of this flag (see the `eventType === 'purchase'` guard below)
// — purchase verification is production-only per
// TASK-INSTRUMENTATION-PACKAGE.md §5.
const isPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview';
const analyticsDebug = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true';
const canPush = isProd || isPreview || analyticsDebug;

/**
 * Returns true if the user has signalled a "Do Not Sell or Share" preference via:
 * - The browser's Global Privacy Control (GPC) signal (navigator.globalPrivacyControl)
 * - A manual opt-out stored in localStorage (gf_do_not_sell)
 *
 * When this returns true, the following are suppressed:
 * - Meta fbq() pixel calls (all 7 events)
 * - TIE/Revenue Roll dataLayer pushes (pushToRevRollLayer)
 * - Google Ads Enhanced Conversions PII fields (user_* in googleDataLayerPurchase)
 * - Stape server-side Meta CAPI (via Sec-GPC header in GTM)
 *
 * NOT affected: GA4 analytics, Firebase analytics, Omnisend, basic Google Ads conversion counting.
 */
export function isDoNotSellSignalActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if ((navigator as any).globalPrivacyControl === true) return true;
    if (localStorage.getItem('gf_do_not_sell') === 'true') return true;
  } catch {
    // localStorage may be blocked in private browsing or by browser policy
  }
  return false;
}

/**
 * Log a one-time console message per page load when the Meta pixel is suppressed
 * due to GPC or manual opt-out. Uses a module-level flag so it fires at most once.
 */
let _gpcLoggedThisLoad = false;
function logGpcSuppressionOnce(): void {
  if (!_gpcLoggedThisLoad) {
    _gpcLoggedThisLoad = true;
    console.info('[GPC] Meta pixel suppressed for this user');
  }
}

/**
 * Generate a unique event_id for Meta pixel + CAPI deduplication.
 * Appends an 8-char random suffix so each occurrence is unique,
 * preventing Meta's 48-hour window from collapsing repeated events
 * (especially critical for anonymous users where uid is empty string).
 *
 * The returned value MUST be generated before fbq() fires and reused
 * for the subsequent trackEvent() / dataLayer push so browser pixel
 * and server CAPI share the same event_id for dedup to work.
 */
export function generateEventId(prefix: string, ...parts: string[]): string {
  const suffix = Math.random().toString(36).substring(2, 10);
  return [prefix, ...parts, suffix].join('_');
}

/**
 * TIE/Revenue Roll Cart ID
 *
 * TIE requires a consistent cart ID across all events for visitor tracking.
 * - For authenticated users: use their Firebase UID
 * - For guests: generate and persist a UUID in localStorage
 */
const TIE_CART_ID_KEY = 'tie_cart_id';

export function getTieCartId(uid?: string): string {
  // If user is authenticated, use their UID
  if (uid && uid.toLowerCase() !== 'guest' && uid !== '') {
    return uid;
  }

  // For guests, use persistent localStorage ID
  // Wrapped in try-catch for private browsing, Safari ITP, or storage blocked scenarios
  if (typeof window !== 'undefined') {
    try {
      let cartId = localStorage.getItem(TIE_CART_ID_KEY);
      if (!cartId) {
        // Generate a UUID-like ID
        cartId = 'guest_' + Math.random().toString(36).substring(2, 15) +
                 Math.random().toString(36).substring(2, 15) +
                 '_' + Date.now().toString(36);
        localStorage.setItem(TIE_CART_ID_KEY, cartId);
      }
      return cartId;
    } catch {
      // localStorage blocked - generate session-only ID
      return 'session_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
    }
  }

  return 'unknown';
}

type FbqFn = {
  (
    type: 'track',
    event: PixelEvents,
    params?: any,
    eventID?: { eventID: string }
  ): void;
  // Manual Advanced Matching transport (GB #1373 fix-forward). Re-init does
  // NOT fire an event by itself — it only updates the pixel instance's
  // stored identity for subsequent track() calls to pick up. See the
  // META_PIXEL_ID doc comment above for why 'set'/'userData' was replaced.
  (type: 'init', pixelId: string, userData?: Record<string, string>): void;
};

const getFbq = (): undefined | FbqFn =>
  typeof window === 'undefined' ? undefined : (window as any).fbq;

/**
 * Logs once per page load when META_PIXEL_ID is unset (neither
 * NEXT_PUBLIC_META_PIXEL_ID nor its NEXT_PUBLIC_FACEBOOK_PIXEL_ID fallback is
 * configured), instead of silently no-op'ing every Advanced Matching call.
 * Expected in local dev and on preview deploys without either env var
 * configured; NOT expected in production — see PR #1436.
 */
let _pixelIdMissingWarned = false;
function warnMissingPixelIdOnce(): void {
  if (!_pixelIdMissingWarned) {
    _pixelIdMissingWarned = true;
    console.debug(
      '[Meta AM] Neither NEXT_PUBLIC_META_PIXEL_ID nor NEXT_PUBLIC_FACEBOOK_PIXEL_ID is set — Advanced Matching identity will not be attached to the browser pixel this page load.'
    );
  }
}

// Note: TIE/Revenue Roll now uses shared dataLayer (as of Dec 2024)
// The window.revrollDataLayer initialization in _document.tsx can be removed in a future cleanup

export async function logEvent(
  eventName: EventTypes,
  eventParams?: {
    coupon?: string;
    currency?: string;
    items?: Item[];
    payment_type?: string;
    value?: number;
    [key: string]: any;
  },
  uid?: string
) {
  if (!isProd) return;

  try {
    // Facebook Pixel / partner-only events
    if (
      eventName === 'add_payment_info' &&
      eventParams?.id &&
      typeof eventParams?.is_payout === 'boolean'
    ) {
      addPaymentInfo({ id: eventParams.id, is_payout: eventParams.is_payout });
    }
    if (eventName === 'add_to_wishlist' && eventParams && uid) {
      addToWishlist(eventParams, uid);
    }
    if (eventName === 'purchase' && eventParams && uid) {
      // RevenueRoll + FB Pixel only. Do NOT push to dataLayer here.
      logPurchase(eventParams, uid);
    }
    if (eventName === 'created_offer' && eventParams?.items?.[0] && uid) {
      logOffer(eventParams.items[0], uid);
    }

    // Push all NON-purchase events to GTM / GA4 as before
    if (eventName !== 'purchase') {
      pushToDataLayer(eventName, {
        ...eventParams,
        user_id: uid,
      });
    }
  } catch (e) {
    logError(e);
  }
}

export async function viewListing(
  item: ProductDocument,
  referrer: string,
  uid?: string
) {
  if (!isProd) return null;
  try {
    // TIE/Revenue Roll - view_item event
    const tieViewItemPayload = {
      rr_cart_id: getTieCartId(uid),
      ecommerce: {
        currencyCode: 'USD',
        detail: {
          products: [
            {
              name: item.title,
              id: item.id,
              // GB #1747: TIE requires variantId to match rr_view_item events
              // to products. Same convention already used for TIE's schema on
              // rr_begin_checkout/rr_purchase (variantSku) and for Omnisend
              // (omnisend.ts:160, $variantID: product_sku || product_id) —
              // sku falls back to the product id when unset.
              variantId: item.sku || item.id,
              image: item.thumbnail,
              price: item.price,
              brand: item.brand,
              variant: item.condition || '',
            },
          ],
        },
      },
    };
    pushToRevRollLayer('rr_view_item', tieViewItemPayload);

    // Fire internal tracking
    httpsCallable(
      functions,
      'products-viewedProduct'
    )({
      item,
      total: item.price,
      from_web: true,
    });

    // Meta dedup: one event_id shared by browser pixel + server CAPI (via dataLayer → Stape).
    // Random suffix makes each occurrence unique — prevents Meta's 48-hour window from
    // collapsing all anonymous visits to the same product into one event.
    const viewEventId = generateEventId('view', uid ?? '', item.id);

    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'ViewContent',
          {
            content_ids: [item.id],
            content_name: item.title,
            content_category: item.categories?.[0],
            content_type: 'product',
            contents: [{ id: item.id, quantity: 1 }],
            value: item.price,
            currency: 'USD',
          },
          { eventID: viewEventId }
        );
      }
    }

    // Omnisend - track product view for browse abandonment & campaign attribution
    omnisendTrackProductViewed(item);

    return viewEventId;
  } catch (e) {
    logError(e);
  }
}

export async function viewMaster(item: MasterDocument) {
  if (!isProd) return null;
  try {
    await httpsCallable(
      functions,
      'master-viewMasterProduct'
    )({
      item,
      from_web: true,
    });

    // TIE/Revenue Roll - view_item event for master pages
    const tieViewItemPayload = {
      rr_cart_id: getTieCartId(),
      ecommerce: {
        currencyCode: 'USD',
        detail: {
          products: [
            {
              name: item.title,
              id: item.id,
              // GB #1747: master/aggregate pages roll up many sellers'
              // listings under one model, so there is no single per-listing
              // SKU here — mpn (manufacturer part number) is the closest
              // model-level equivalent, falling back to the master doc id.
              variantId: item.mpn || item.id,
              image: item.images?.[0],
              brand: item.brand,
              variant: item.model || '',
            },
          ],
        },
      },
    };
    pushToRevRollLayer('rr_view_item', tieViewItemPayload);
  } catch (e) {
    logError(e);
  }
}

export async function viewBlogPost(item: BlogDocument, uid?: string) {
  if (!isProd) return null;
  try {
    // Ensure blog has a valid ID - use slug as fallback for WordPress posts
    const blogItem = {
      ...item,
      id: item.id || `wp-${item.slug}`,
    };

    await httpsCallable(
      functions,
      'blog-viewedBlog'
    )({
      item: blogItem,
      from_web: true,
    });

    pushToDataLayer('view_blog', {
      user_id: uid || null,
      title: item.title,
      url: `https://gearfocus.com/blog/${item.slug}`,
      categories: item.categories,
      tags: item.tags,
    });
  } catch (e) {
    logError(e);
  }
}

export async function clickAffiliate(
  collection: string,
  document: string,
  source: string
) {
  if (!isProd) return null;
  try {
    await httpsCallable(
      functions,
      'affiliate-clickAffiliateLink'
    )({
      collection,
      document,
      source,
      from_web: true,
    });
  } catch (e) {
    logError(e);
  }
}

export function clickBlogAd(
  adId: string,
  adType: string,
  adTitle: string,
  adUrl: string,
  vendor: string,
  blogSlug?: string
) {
  if (!isProd) return;
  try {
    pushToDataLayer('blog_ad_click', {
      ad_id: adId || 'unknown',
      ad_type: adType,
      ad_title: adTitle,
      ad_url: adUrl,
      vendor: vendor || 'unknown',
      blog_slug: blogSlug || null,
    });
  } catch (e) {
    logError(e);
  }
}

export async function logInitCheckout(
  items: CartItem[],
  total: number,
  uid?: string
) {
  if (!isProd) return;
  try {
    httpsCallable(
      functions,
      'orders-initiateCheckout'
    )({
      items,
      total,
      isComplete: false,
      from_web: true,
    });

    // TIE/Revenue Roll - begin_checkout event
    const tieMappedItems = items.map((item) => ({
      name: item.product_title,
      id: item.product_id,
      variantSku: item.product_sku,
      image: item.product_image,
      price: item.product_cost,
      brand: item.product_brand,
      variant: '',
    }));

    pushToRevRollLayer('rr_begin_checkout', {
      rr_cart_id: getTieCartId(uid),
      ecommerce: {
        currencyCode: 'USD',
        checkout: {
          actionField: { step: 1 },
          products: tieMappedItems,
        },
      },
    });

    // Meta dedup: one event_id shared by browser pixel + server CAPI (via dataLayer → Stape).
    // Random suffix makes each occurrence unique per checkout attempt.
    const checkoutEventId = generateEventId('checkout', uid ?? '', items[items.length - 1]?.product_id ?? '');

    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'InitiateCheckout',
          {
            content_ids: items.map((i) => i.product_id),
            contents: items.map((i) => ({ id: i.product_id, quantity: 1 })),
            num_items: items.length,
            value: total,
            currency: 'USD',
          },
          { eventID: checkoutEventId }
        );
      }
    }

    // Omnisend - track checkout start for checkout abandonment & campaign attribution
    omnisendTrackStartedCheckout(items, total);

    return checkoutEventId;
  } catch (e) {
    logError(e);
  }
}

export async function logAddToCart(
  item: ProductDocument,
  total: number,
  uid: string
) {
  if (!isProd) return;
  try {
    // Meta dedup: one event_id shared by browser pixel + server CAPI (via dataLayer → Stape).
    // Random suffix makes each occurrence unique per add-to-cart action.
    const cartEventId = generateEventId('addcart', uid, item.id);

    // Fire pixel and dataLayer events FIRST — these must not be blocked by Cloud Function latency
    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'AddToCart',
          {
            content_ids: [item.id],
            content_name: item.title,
            content_type: 'product',
            contents: [{ id: item.id, quantity: 1 }],
            value: total,
            currency: 'USD',
          },
          { eventID: cartEventId }
        );
      }
    }

    // TIE/Revenue Roll - add_to_cart event
    pushToRevRollLayer('rr_add_to_cart', {
      rr_cart_id: getTieCartId(uid),
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [
            {
              name: item.title,
              id: item.id,
              image: item.thumbnail,
              price: item.price,
              brand: item.brand,
              variant: item.condition || '',
              quantity: 1,
            },
          ],
        },
      },
    });

    // Omnisend - track add to cart for cart abandonment & campaign attribution
    omnisendTrackAddedToCart(item, 1);

    // Cloud Function for Omnisend event + Mixpanel — fire-and-forget (no await)
    httpsCallable<Record<string, never>, void>(
      functions,
      'addToCart'
    )({}).catch(() => {
      // Silently swallow — Cloud Function fails for guests (authCheck) and
      // cold starts. Pixel events already fired above; this is best-effort.
    });

    return cartEventId;
  } catch (e) {
    logError(e);
  }
}

export async function completeRegistration(
  userId: string,
  userEmail: string,
  method: string
) {
  const callCompleteRegistration = httpsCallable<
    { email: string; uid: string; method: string },
    any
  >(functions, 'users-completeUserRegistration');
  try {
    await callCompleteRegistration({ email: userEmail, uid: userId, method });
  } catch (error: unknown) {
    // Callable error text and details can echo signup data. Keep diagnostics value-free.
    const code = (error as { code?: unknown } | null)?.code;
    const message = (error as { message?: unknown } | null)?.message;
    console.error('[completeRegistration] Firebase callable error', {
      code:
        typeof code === 'string' && /^[a-z0-9_./-]{1,64}$/i.test(code)
          ? code
          : 'unknown',
      messageLength: typeof message === 'string' ? message.length : 0,
    });
  }
}

export async function becomeASeller(uid: string, email: string) {
  try {
    pushToDataLayer('become_a_seller', { user_id: uid, email });
  } catch (e) {
    logError(e);
  }
}

function logPurchase(
  eventParams: {
    items?: Item[];
    customer?: {
      name: string;
      email: string;
      phone: string | null;
      country: string;
      region: string;
      city: string;
      zip: string;
    };
    transaction?: {
      id: string;
      sellers: string[];
      total: number;
      tax: number;
      shipping: number;
      coupon?: string;
      discount?: number;
    };
    userId?: string;
  },
  uid: string
) {
  if (!isProd) return;

  try {
    const tieUserId = eventParams.userId ?? undefined;

    // TIE/Revenue Roll - purchase event (matches their schema)
    const tieMappedItems =
      eventParams.items?.map((item) => ({
        name: item.item_name ?? '',
        id: item.item_id ?? '',
        variantSku: item.item_id ?? '', // Use item_id as SKU fallback
        image: item.item_thumbnail ?? '',
        price: item.price ?? 0,
        brand: item.item_brand ?? '',
        variant: '',
      })) ?? [];

    // Split name into first_name and last_name for TIE
    const fullName = eventParams.customer?.name || '';
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // TIE purchase payload matching their schema
    pushToRevRollLayer('rr_purchase', {
      rr_cart_id: getTieCartId(tieUserId),
      first_name: firstName,
      last_name: lastName,
      email_address: eventParams.customer?.email || '',
      phone_number: eventParams.customer?.phone || '',
      customer_id: tieUserId || uid,
      country: eventParams.customer?.country || '',
      region: eventParams.customer?.region || '',
      city: eventParams.customer?.city || '',
      zip: eventParams.customer?.zip || '',
      ecommerce: {
        currencyCode: 'USD',
        purchase: {
          actionField: {
            id: eventParams.transaction?.id || '',
            affiliation: 'GearFocus',
            revenue: eventParams.transaction?.total || 0,
            tax: eventParams.transaction?.tax || 0,
            shipping: eventParams.transaction?.shipping || 0,
            coupon: eventParams.transaction?.coupon || '',
            discount_amount: eventParams.transaction?.discount || 0,
          },
          products: tieMappedItems,
        },
      },
    });

    // Facebook Pixel
    // Purchase event_id stays deterministic — NO random suffix.
    // googleDataLayerPurchase() in google.service.ts pushes event_id: `purchase_${txId}`
    // to dataLayer, which Stape CAPI reads. Adding a suffix to fbq only would cause
    // browser pixel / server CAPI mismatch → double-counted purchase conversions.
    // txId (Stripe transaction ID) is already unique per purchase so no dedup risk.
    const txId = eventParams.transaction?.id ?? '';
    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        // F2 / SPEC-3: attach Manual Advanced Matching identity to the browser
        // pixel before firing Purchase. Every purchaser (guest or auth) has
        // typed an email by this point, so this is the highest-coverage call
        // site for this fix. Uses the same customer object and normalizer
        // (normalizeUserForDL) already used for the server CAPI Purchase leg.
        //
        // Isolated in its own try/catch (adversarial review CPO M2): this
        // call sits inside the outer logPurchase try, and the Purchase
        // fbq('track', ...) call below is the highest-value line in this
        // whole function. A throw here must never cost the Purchase fire —
        // an EMQ-lift misfire is acceptable, a dropped conversion event is not.
        try {
          setMetaAdvancedMatching(normalizeUserForDL(eventParams.customer));
        } catch (amError) {
          logError(amError);
        }
        fbq(
          'track',
          'Purchase',
          {
            value: eventParams.transaction?.total,
            currency: 'USD',
            content_ids: eventParams.items?.map(item => item.item_id ?? '') ?? [],
            contents: eventParams.items?.map(item => ({ id: item.item_id ?? '', quantity: item.quantity ?? 1 })) ?? [],
            num_items: eventParams.items?.length ?? 0,
            content_type: 'product',
          },
          { eventID: `purchase_${txId}` }
        );
      }
    }
  } catch (e) {
    logError(e);
  }
}

function addPaymentInfo(eventParams: { id: string; is_payout: boolean }) {
  try {
    const paymentEventId = generateEventId('payment', eventParams.id);
    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'AddPaymentInfo',
          {
            content_ids: [eventParams.id],
            content_category: eventParams.is_payout
              ? 'For Payout'
              : 'For Purchase',
          },
          { eventID: paymentEventId }
        );
      }
    }
  } catch (e) {
    logError(e);
  }
}

function addToWishlist(
  eventParams: {
    currency?: string;
    items?: Item[];
    value?: number;
  },
  uid: string
) {
  try {
    const itemId = eventParams.items?.[0]?.item_id ?? '';
    const wishlistEventId = generateEventId('wishlist', uid, itemId);
    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'AddToWishlist',
          {
            content_ids: [itemId],
            content_name: eventParams.items?.[0]?.item_name ?? '',
            value: eventParams.value ?? 0,
            currency: eventParams.currency ?? 'USD',
          },
          { eventID: wishlistEventId }
        );
      }
    }
  } catch (e) {
    logError(e);
  }
}

function logOffer(item: Item, uid: string) {
  try {
    const leadEventId = generateEventId('lead', uid, item.item_id ?? '');
    const fbq = getFbq();
    if (fbq) {
      if (isDoNotSellSignalActive()) {
        logGpcSuppressionOnce();
      } else {
        fbq(
          'track',
          'Lead',
          {
            content_category: item.item_categories,
            content_name: item.item_name,
            value: item.price,
            currency: 'USD',
          },
          { eventID: leadEventId }
        );
      }
    }
  } catch (e) {
    logError(e);
  }
}

export const getItems = (items: ProductDocument[]) =>
  items.map((i) => getItem(i));

const getItem = (i: ProductDocument) => ({
  item_id: i.id,
  item_name: i.title,
  item_brand: i.brand || '',
  item_categories: i.categories,
  item_thumbnail: i.thumbnail,
  item_slug: i.slug,
  master: i.gearMaster?.[0] || '',
  external_source: i.external_source || 'gearfocus',
  price: i.price,
  quantity: 1,
});

export function pushToDataLayer(
  eventName: string,
  payload: Record<string, unknown>
) {
  if (
    typeof window !== 'undefined' &&
    Array.isArray((window as any).dataLayer)
  ) {
    const dl = (window as any).dataLayer as Array<Record<string, any>>;
    dl.push({ ecommerce: null, search_term: null, method: null });
    // Signal GPC/manual opt-out to Stape server-side GTM so it can block
    // Meta CAPI tags for manual opt-outs (non-GPC browsers like Chrome).
    // GPC browsers send Sec-GPC:1 header automatically, but localStorage
    // opt-outs have no header — this dataLayer flag bridges the gap.
    const gpcFlag = isDoNotSellSignalActive() ? { gpc_active: true } : {};
    dl.push({ event: eventName, ...payload, ...gpcFlag });
  }
}

/**
 * Push events to dataLayer for TIE/Revenue Roll tracking.
 *
 * As of Dec 2024, TIE uses the shared dataLayer (same as GTM) instead of
 * a separate revrollDataLayer. This allows TIE to see custom events like
 * rr_purchase with enriched customer data for visitor identification.
 *
 * Note: These rr_* events use a different format than GA4 events and
 * won't be processed by the GA4 Measurement Protocol.
 */
export function pushToRevRollLayer(
  eventName: string,
  payload: Record<string, unknown>
) {
  // TIE/Revenue Roll identifies visitors for Meta retargeting — suppress when GPC is active
  if (isDoNotSellSignalActive()) {
    logGpcSuppressionOnce();
    return;
  }
  if (
    typeof window !== 'undefined' &&
    Array.isArray((window as any).dataLayer)
  ) {
    const dl = (window as any).dataLayer as Array<Record<string, any>>;
    dl.push({ ecommerce: null, search_term: null, method: null });
    dl.push({ event: eventName, ...payload });
  }
}

/**
 * Guest identity captured from a value the guest already typed for a
 * stated, different purpose (checkout email). Session-scoped only — never
 * persisted across browser sessions, and never sourced from Notify-Me,
 * Make-an-Offer, or Contact-Us emails (TASK-1373 §3: those users never
 * agreed to be matched against an advertising profile).
 */
export type GuestIdentity = {
  email: string;
  first?: string;
  last?: string;
};

/** Storage-layer shape only — `capturedAt` never leaves getGuestIdentity(),
 *  callers always deal in plain GuestIdentity. */
type StoredGuestIdentity = GuestIdentity & {
  capturedAt: number;
};

const GUEST_IDENTITY_STORAGE_KEY = 'gf_guest_identity';

/**
 * Ceiling on how long a captured-but-un-cleared guest identity may be
 * reused (Codex re-review, TASK-1373 / GB #1206). A shopper who blurs an
 * email and then ABANDONS — no conversion, so markConverted() never runs
 * clearGuestIdentity() — leaves that email sitting in sessionStorage
 * indefinitely otherwise. On a shared or un-closed device, a LATER
 * shopper's anonymous fire-time fallback (30s timer / field-interaction
 * fallback, firing before they've typed their own email) would read and
 * attach the PRIOR shopper's identity. 30 minutes covers a legitimate
 * same-shopper return-to-tab without leaving a stale identity attachable
 * indefinitely.
 */
const GUEST_IDENTITY_TTL_MS = 30 * 60 * 1000;

/**
 * Persist the guest's checkout email (and name, when known) for the rest of
 * this browser session — same sessionStorage pattern as the `gf_checkout_pi`
 * guard in checkout-v3.tsx. Session-scoped by design: guests are not
 * identified across browser sessions. Stamps a capture timestamp so
 * getGuestIdentity() can bound how long this is reusable — see its docs.
 *
 * Caller is responsible for the GPC/opt-out check (see
 * `isDoNotSellSignalActive`) before calling this — no PII should be
 * persisted locally for a user who opted out, even though it never leaves
 * the browser here.
 */
export function setGuestIdentity(identity: GuestIdentity): void {
  if (typeof window === 'undefined') return;
  try {
    const stored: StoredGuestIdentity = { ...identity, capturedAt: Date.now() };
    sessionStorage.setItem(GUEST_IDENTITY_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage may be blocked (private browsing, storage policy) — non-fatal
  }
}

/**
 * Returns the currently-stored guest identity, or null if none exists or
 * it's older than GUEST_IDENTITY_TTL_MS (30 minutes).
 *
 * Residual, accepted (Codex re-review, TASK-1373 / GB #1206): a
 * SAME-shopper return to the SAME TAB within the TTL will still
 * legitimately reuse their own stored identity — that's the intended,
 * bounded case this TTL exists to allow, not to close. What remains a real
 * (bounded, low-probability) risk is a DIFFERENT shopper on the same
 * un-closed tab within 30 minutes of the prior shopper's abandonment. This
 * repo has no reliable "shopper left without converting" signal to clear on
 * (no beforeunload-based clear — those fire on normal navigation too, which
 * would wrongly wipe identity for a shopper mid-checkout who e.g. opens a
 * new tab to check something). The TTL bounds the exposure window instead
 * of eliminating it.
 */
export function getGuestIdentity(): GuestIdentity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(GUEST_IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.email !== 'string') return null;
    const capturedAt = typeof parsed?.capturedAt === 'number' ? parsed.capturedAt : 0;
    if (Date.now() - capturedAt > GUEST_IDENTITY_TTL_MS) return null;
    const { email, first, last } = parsed as StoredGuestIdentity;
    return { email, ...(first ? { first } : {}), ...(last ? { last } : {}) };
  } catch {
    return null;
  }
}

/**
 * Clear a previously-captured guest identity. The stored email never
 * transmits anywhere on its own — every consumer re-checks
 * `isDoNotSellSignalActive()` at read/push time — but it has no reason to
 * keep sitting in sessionStorage once the user opts out mid-session. Call
 * this from the privacy opt-out flow (PrivacyPolicyPage's handleOptOut).
 */
export function clearGuestIdentity(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(GUEST_IDENTITY_STORAGE_KEY);
  } catch {
    // sessionStorage may be blocked (private browsing, storage policy) — non-fatal
  }
}

export type EcommerceEventType =
  | 'view_item_list'
  | 'select_item'
  | 'view_item'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'
  | 'purchase'
  | 'refund'
  // GB #1410 (I3): splits the begin_checkout → add_shipping_info window
  // into address-entry friction (before shipping_step_view) vs cost shock
  // (after it, before add_shipping_info).
  | 'shipping_step_view'
  | 'payment_step_view';

/**
 * Extract user data fields for dataLayer pushes (Enhanced Conversions / Meta CAPI).
 * For guests (no userDoc/email), falls back to a pseudonymous first-party
 * external_id (GB #1373 / F3) so guest events aren't fully anonymous to Meta.
 * Only includes fields that exist. Field names match the server GTM variable
 * keyPaths set in the Feb 3 fix.
 */
export const getUserDataForDL = (
  userDoc?: {
    uid?: string;
    email?: string;
    phone?: string;
    first?: string;
    last?: string;
    addresses?: Array<{
      city_locality?: string;
      state_province?: string;
      postal_code?: string;
      country_code?: string;
      is_default?: boolean;
    }>;
  } | null
): Record<string, string> | null => {
  if (isDoNotSellSignalActive()) return null;
  if (!userDoc?.email) {
    // Guest (or userDoc not yet resolved): no email to send, but getTieCartId()
    // mints a first-party, non-PII id for guests, persisted in localStorage and
    // reused across every event in the session/across sessions — EXCEPT when
    // localStorage is blocked (private browsing, storage policy), in which case
    // it returns a fresh 'session_'-prefixed id on every single call. A fresh id
    // per event is not a join key, it's noise, so exclude that prefix. 'unknown'
    // means getTieCartId() had no window to work with (SSR) — also nothing usable.
    const guestId = getTieCartId();
    const usable = guestId && guestId !== 'unknown' && !guestId.startsWith('session_');
    return usable ? { external_id: guestId } : null;
  }
  const result: Record<string, string> = {};
  // Firebase UID as external_id for Meta CAPI user matching (Stape auto-hashes via SHA-256)
  if (userDoc.uid) result.external_id = userDoc.uid;
  const email = userDoc.email.trim().toLowerCase();
  if (email) result.user_email = email;
  const phone = typeof userDoc.phone === 'string' ? userDoc.phone.replace(/[^\d+]/g, '') : undefined;
  if (phone) result.user_phone = phone;
  if (userDoc.first) result.user_first_name = userDoc.first;
  if (userDoc.last) result.user_last_name = userDoc.last;
  // Address data from default or primary address — improves Meta CAPI Event Match Quality
  const addr = userDoc.addresses?.find(a => a.is_default) ?? userDoc.addresses?.[0];
  if (addr?.city_locality) result.user_city = addr.city_locality;
  if (addr?.state_province) result.user_region = addr.state_province;
  if (addr?.postal_code) result.user_postal_code = addr.postal_code;
  if (addr?.country_code) result.user_country = addr.country_code;
  return Object.keys(result).length ? result : null;
};

/**
 * Normalize a purchase `customer` object (name/email/phone/country/region/city/zip
 * — the shape used by logPurchase() and googleDataLayerPurchase()) into the same
 * dataLayer field names getUserDataForDL() produces for userDoc-shaped input.
 *
 * Moved here (was previously local to google.service.ts) so both the Purchase
 * CAPI path (google.service.ts) and the Purchase Manual Advanced Matching path
 * (logPurchase() below) share one normalizer instead of two copies that could
 * drift apart. No behavior change to the existing normalization logic.
 */
export const normalizeUserForDL = (cust?: any): Record<string, string> | null => {
  if (!cust) return null;
  const email =
    typeof cust.email === 'string' ? cust.email.trim().toLowerCase() : undefined;
  const phone =
    typeof cust.phone === 'string' ? cust.phone.replace(/[^\d+]/g, '') : undefined;
  const [first_name, ...rest] = (cust.name || '').trim().split(/\s+/);
  const last_name = rest.join(' ') || undefined;

  return {
    ...(email ? { user_email: email } : {}),
    ...(phone ? { user_phone: phone } : {}),
    ...(first_name ? { user_first_name: first_name } : {}),
    ...(last_name ? { user_last_name: last_name } : {}),
    ...(cust?.country ? { user_country: cust.country } : {}),
    ...(cust?.region ? { user_region: cust.region } : {}),
    ...(cust?.city ? { user_city: cust.city } : {}),
    ...(cust?.zip ? { user_postal_code: cust.zip } : {}),
  };
};

/**
 * Map dataLayer-shaped user data (getUserDataForDL / normalizeUserForDL output)
 * to Meta's official browser-pixel Advanced Matching parameter names.
 * https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
 *
 * Values stay in normalized plaintext (trimmed/lowercased email, digits-only
 * phone) — NOT pre-hashed. This is deliberate: the Meta Pixel JS SDK hashes
 * known Advanced Matching fields (em, ph, fn, ln, ct, st, zp, country,
 * external_id) client-side with SHA-256 before transmission, per Meta's
 * documented format for fbq('init', pixelId, {...}). This is a different
 * transport than the CAPI path (dataLayer → Stape), where Stape performs the
 * hashing server-side — both end up SHA-256 hashed before they reach Meta,
 * just via different hashers on different legs.
 */
function toMetaAdvancedMatchingParams(
  dlUserData: Record<string, string> | null
): Record<string, string> | null {
  if (!dlUserData) return null;
  const am: Record<string, string> = {};
  if (dlUserData.user_email) am.em = dlUserData.user_email;
  if (dlUserData.user_phone) am.ph = dlUserData.user_phone;
  if (dlUserData.user_first_name) am.fn = dlUserData.user_first_name;
  if (dlUserData.user_last_name) am.ln = dlUserData.user_last_name;
  if (dlUserData.user_city) am.ct = dlUserData.user_city;
  if (dlUserData.user_region) am.st = dlUserData.user_region;
  if (dlUserData.user_postal_code) am.zp = dlUserData.user_postal_code;
  if (dlUserData.user_country) am.country = dlUserData.user_country;
  if (dlUserData.external_id) am.external_id = dlUserData.external_id;
  return Object.keys(am).length ? am : null;
}

/**
 * N1 retry latch (post-#1409 review, GB #1373). AuthContext's userDoc
 * snapshot can resolve BEFORE the Meta pixel script exists — it loads via
 * GTM off the slower Stape domain — most visibly on a returning
 * pre-authenticated user's first page load. Without this, the plain
 * `if (!fbq) return` below silently drops that identity for the rest of the
 * session (until the next userDoc snapshot, e.g. the ~hourly token refresh).
 *
 * Module-level (not per-call) so:
 *  - a later setMetaAdvancedMatching() call can cancel/replace an earlier
 *    still-pending one (last write wins — see cancelPendingAdvancedMatching()
 *    at the top of setMetaAdvancedMatching()), and
 *  - clearMetaAdvancedMatching() can always find and cancel it, so a queued
 *    identity set can never fire AFTER a logout/opt-out has already run.
 *    That ordering (clear beats pending) is the load-bearing invariant here:
 *    the alternative — a stale identity attaching itself to a new anonymous
 *    session on a shared/kiosk browser — is a PII leak, not just a bug.
 */
let pendingAmParams: Record<string, string> | null = null;
let pendingAmTimer: ReturnType<typeof setInterval> | null = null;
let pendingAmDeadline = 0;

const AM_RETRY_INTERVAL_MS = 500;
const AM_RETRY_TIMEOUT_MS = 30_000;

function cancelPendingAdvancedMatching(): void {
  if (pendingAmTimer !== null) {
    clearInterval(pendingAmTimer);
    pendingAmTimer = null;
  }
  pendingAmParams = null;
}

/**
 * M1 (adversarial CONDITIONAL fix, GB #1373): defends against env/GTM
 * drift — NEXT_PUBLIC_META_PIXEL_ID pointing at a pixel id GTM never
 * actually initialized on this page (e.g. a stale env value after a GTM
 * container pixel swap, or a misconfigured preview environment). Calling
 * fbq('init', wrongId, ...) in that case creates a SEPARATE, phantom pixel
 * instance rather than re-initializing the real one — silently fragmenting
 * tracking instead of attaching identity to it.
 *
 * fbevents.js exposes NO public, documented introspection API for "which
 * pixel ids are currently initialized." The path used here —
 * fbq.__fbeventsModules.fbevents().getState().pixels, an ARRAY of
 * { id, ... } entries — is a community-reverse-engineered internal
 * (https://www.thyngster.com/proper-methodtips-setting-facebook-pixels-no-errors),
 * not a Meta-published contract. It can change or disappear in a future
 * fbevents.js build without notice. Every access below is defensive
 * (optional chaining + try/catch): if the shape isn't what's expected, this
 * treats it as "can't determine" and lets the init proceed rather than
 * blocking the whole feature on an introspection gap — Meta's own
 * "Duplicate Pixel ID" console warning (or the rejection class of warning
 * that sank the 'set' transport) remains the fallback signal for a real
 * drift in that case.
 */
function isPixelIdDrifted(fbq: FbqFn, pixelId: string): boolean {
  try {
    const internals = fbq as unknown as {
      __fbeventsModules?: {
        fbevents?: () => { getState?: () => { pixels?: unknown } } | undefined;
      };
    };
    const state = internals.__fbeventsModules?.fbevents?.()?.getState?.();
    const pixels = state?.pixels;
    if (!Array.isArray(pixels) || pixels.length === 0) {
      return false; // can't determine -> don't block the feature
    }
    const initializedIds = pixels
      .map((p) => (p && typeof p === 'object' ? (p as { id?: unknown }).id : undefined))
      .filter((id): id is string => typeof id === 'string');
    if (initializedIds.length === 0) {
      return false; // unexpected shape -> can't determine -> don't block
    }
    const drifted = !initializedIds.includes(pixelId);
    if (drifted) {
      console.warn(
        `[Meta AM] ${META_PIXEL_ID_SOURCE} (${pixelId}) does not match any pixel GTM initialized on this page (${initializedIds.join(', ')}). Skipping Advanced Matching init to avoid creating a phantom second pixel instance — check for GTM/env drift.`
      );
    }
    return drifted;
  } catch {
    return false; // introspection failed -> don't block the feature
  }
}

/**
 * Tracks whether Advanced Matching identity was actually attached to the
 * BROWSER pixel THIS page load — set true only right after a non-throwing
 * fbq('init', pixelId, amParams) call actually fires (immediate or a
 * later-firing retry; see setMetaAdvancedMatching below). Stays false when
 * the call never happens: GPC/opt-out active, pixel id unset, pixel id
 * drifted, or the init call threw.
 *
 * Exists for one reason: Phase A-E wire experiments (2026-07-15, live on
 * gearfocus.com — see clearMetaAdvancedMatching's doc comment and memory
 * note reference_meta_pixel_first_write_wins) proved there is NO in-page way
 * to clear Advanced Matching identity once attached. The only real clear is
 * a hard navigation. Callers that need a guaranteed-clean pixel (logout,
 * guest purchase confirmation, cross-tab opt-out sync) use this flag to
 * decide whether a hard nav is actually necessary, instead of forcing one on
 * every tab/session regardless of whether identity was ever attached.
 */
let identityAttachedThisPage = false;

/**
 * Public read of identityAttachedThisPage — see that variable's doc comment.
 * Used by AuthContext (logout), CheckoutProcessV3/ExpressCheckoutButton
 * (guest purchase confirmation nav), and the cross-tab opt-out listener
 * below to gate a hard navigation.
 */
export function isPixelIdentityAttached(): boolean {
  return identityAttachedThisPage;
}

// Belt-and-suspenders in-memory guard (Codex P2, final round, GB #1373):
// see shouldReloadAfterGuestIdentityAttach's doc comment below for why this
// exists alongside the sessionStorage once-key, not instead of it.
const reloadedOrderIds = new Set<string>();

/**
 * F2 fix (post-merge review, GB #1373). Decides whether an order-
 * confirmation page must hard-reload ONCE after redirect-return guest
 * purchase tracking (Affirm/3DS — Stripe/Affirm's own return_url lands on
 * a FRESH page load, so CheckoutProcessV3's hard-nav-on-attach logic never
 * ran for this order) attaches Advanced Matching identity with no
 * downstream clear. Phase A-E wire evidence: no in-page clear exists (see
 * clearMetaAdvancedMatching's doc comment above) — the identity would
 * otherwise ride all subsequent anonymous browsing on this tab/device,
 * same shared-device leak class the hard-nav design exists to close.
 *
 * True only for a guest (`!isAuthenticated`) whose identity actually
 * attached THIS page (isPixelIdentityAttached()) AND who hasn't already
 * been reloaded once for this exact order. Impure by design: the
 * check-and-claim of the once-key happens atomically in the SAME call
 * (read, then immediately write) specifically so two near-simultaneous
 * calls can't both see "not yet reloaded" and both trigger a reload —
 * whichever call runs first claims the key, the other sees it already set.
 * Returns false (and does not touch sessionStorage) once the once-key
 * already exists, or if sessionStorage itself is blocked (throws on
 * access — private browsing / policy) — no way to loop-guard safely in
 * that case, so skip the reload rather than risk one.
 *
 * Belt-and-suspenders in-memory guard (Codex P2, final round): a
 * sessionStorage-only once-key assumes writes actually persist. Some
 * privacy-hardened browsers/extensions instead make `setItem` a silent
 * no-op — it doesn't throw, but the value never actually saves, so a later
 * `getItem` still returns null. Under that condition the sessionStorage
 * check ALONE would see "never reloaded" on every call and return true
 * forever — a genuine reload loop, not just a missed clear. The in-memory
 * `reloadedOrderIds` Set can't be silently defeated the same way (a normal
 * JS `Set.add()` always succeeds), so it's checked FIRST. It only protects
 * calls within THIS page's lifetime — a fresh page load gets a fresh, empty
 * Set, same as any other module state — sessionStorage is still what's
 * meant to survive ACROSS reloads; this is the fallback for repeated calls
 * within one load (e.g. an effect re-running before the reload actually
 * takes effect).
 *
 * On the NORMAL same-tab flow (CheckoutProcess already tracked this order
 * before this page loaded), isPixelIdentityAttached() stays false on this
 * fresh page load (nothing attached identity here), so this naturally
 * returns false without ever touching either guard.
 */
export function shouldReloadAfterGuestIdentityAttach(
  orderId: string,
  isAuthenticated: boolean
): boolean {
  if (isAuthenticated || !isPixelIdentityAttached()) return false;
  if (reloadedOrderIds.has(orderId)) return false;
  try {
    const onceKey = `gf_conf_reloaded_${orderId}`;
    if (sessionStorage.getItem(onceKey)) return false;
    sessionStorage.setItem(onceKey, '1');
    reloadedOrderIds.add(orderId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Codex finding (final round, GB #1373). `omnisendTrackPurchase()` fires
 * unconditionally on both order-confirmation pages ("ALWAYS fire — not
 * called from CheckoutProcess"), and its own dedup does NOT survive the F2
 * reload: `sentRef` is in-memory React state, which dies with the page on
 * reload, and the `analytics_fired` Firestore write is permission-blocked
 * for guests (the pages' own comments already say so — guest writes fail
 * silently, dedup for guests relies on sessionStorage instead, which
 * Omnisend never checks). Post-reload, the EXISTING GA4
 * `ga4_purchase_<txId>` sessionStorage key correctly suppresses
 * `logEvent`/`googleDataLayerPurchase` from re-firing (via
 * `alreadyTrackedFromCheckout`) — but nothing stopped Omnisend from firing
 * a SECOND time. Omnisend drives email automations: a redirect-return
 * guest's purchase double-firing there risks a duplicate purchase email to
 * a real customer.
 *
 * Fix: gate the Omnisend call with `shouldFireOmnisendPurchase(shouldReload)`
 * at both confirmation pages' call sites — skip it on the PRE-reload pass
 * (`shouldReload` true; the reload is imminent, so let the post-reload pass
 * own it) and fire it on the POST-reload pass (`shouldReload` is false
 * there — the once-key is already claimed, so
 * `shouldReloadAfterGuestIdentityAttach` returns false, and `sentRef` is
 * fresh on the new page load, so the tracking block runs normally). Every
 * non-reload flow (the overwhelming majority — normal same-tab checkout,
 * authenticated purchasers, GPC-active guests) keeps the current
 * always-fire semantics unchanged, since `shouldReload` is false in all of
 * those cases too.
 *
 * Deliberately extracted as its own function — trivial as the logic looks
 * — because the two order-confirmation pages have no test scaffolding
 * (heavy Firestore/Auth/cart dependencies, no existing test file for
 * either), so this is the only level at which the regression is directly
 * testable. The page-level wiring (calling this function, then calling
 * `omnisendTrackPurchase` based on its result) is not covered by a
 * component-level test — same class of gap already flagged for this
 * function's sibling wiring (the reload call itself, the
 * `googleDataLayerPurchase` timing).
 */
export function shouldFireOmnisendPurchase(shouldReload: boolean): boolean {
  return !shouldReload;
}

/**
 * Manual Advanced Matching (F2, GB #1373 / TASK-1373-META-EMQ.md SPEC-3).
 *
 * Supplies user_data to the BROWSER Meta pixel via a re-init —
 * fbq('init', PIXEL_ID, userData) — so browser-sourced events (ViewContent,
 * AddToCart, InitiateCheckout, Purchase) carry identity, not just the server
 * CAPI leg. Per Meta's docs, this is the documented Manual Advanced Matching
 * transport: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching.
 * Community implementation guides (e.g. trackingchef.com's Advanced Matching
 * writeup) describe this exact re-init-after-identification pattern as
 * standard for GTM setups where the base pixel tag inits without identity
 * before ours is known — "if user identification happened after the pixel
 * has been initialized, you can trigger a second init call and subsequent
 * events will carry the user data parameters."
 *
 * NOT the same as `fbq('set', 'userData', ...)`, which this replaces — that
 * transport is REJECTED by Meta's current pixel build (live-confirmed
 * 2026-07-15, see the META_PIXEL_ID doc comment above and the PR #1409
 * finding comment). Re-init does not fire an event on its own; it updates
 * the pixel instance's stored identity, which every subsequent
 * fbq('track', ...) call on the page automatically includes. None of the
 * existing fbq('track', ...) call sites or their {eventID} values change as
 * a result of this function existing.
 *
 * RESOLVED by live wire experiments, 2026-07-15 (Phase A-E, on
 * gearfocus.com — see PR #1436 for the full evidence table): a second init
 * call DOES attach identity — ATTACH is proven working, not merely
 * documented-and-assumed. The corollary, also wire-proven: Meta's pixel is
 * FIRST-WRITE-WINS. Once a field is set by an init call, no LATER init call
 * — same field, empty string, or a different value — overwrites it in-page.
 * That has a direct consequence for clearing: see clearMetaAdvancedMatching's
 * doc comment below. Do not re-litigate either finding without new wire
 * evidence; both were tested directly, not inferred from documentation.
 *
 * This is MANUAL Advanced Matching, not Automatic. `enable_automatic_matching`
 * stays false. We choose exactly which fields to send, sourced from the same
 * normalizer already used for CAPI (getUserDataForDL / normalizeUserForDL), under
 * the same GPC/opt-out guard as every other Meta call in this file. Automatic
 * Advanced Matching is Meta's browser-side form scraper and is banned separately
 * — see TASK-1373-META-EMQ.md §0.1. Do not pass `true` to `enable_automatic_matching`
 * anywhere as a "fix" for this; that is a different, disallowed mechanism.
 *
 * Caller is responsible for checking isDoNotSellSignalActive() before calling —
 * this function also checks it independently as a second guard, so a bad call
 * site can't leak PII to the pixel.
 *
 * N1 fix: if fbq isn't on window yet, the identity is queued and retried on a
 * bounded interval (every AM_RETRY_INTERVAL_MS, up to AM_RETRY_TIMEOUT_MS)
 * instead of being silently dropped. See the pendingAmParams doc above for
 * the cancellation/ordering invariants this depends on. Transport-agnostic:
 * this latch behaves identically regardless of which fbq command carries the
 * identity, which is why the 'set'->'init' swap needed no changes here.
 *
 * M1 fix (adversarial CONDITIONAL, GB #1373): every init call — immediate or
 * retried — is guarded by isPixelIdDrifted() first. See that function's doc
 * comment for why: a stale NEXT_PUBLIC_META_PIXEL_ID (drifted from whatever
 * GTM actually initialized) must never init a phantom second pixel.
 */
export function setMetaAdvancedMatching(
  dlUserData: Record<string, string> | null
): void {
  // Last write wins: any new call — including a retry of the SAME identity
  // via a re-fired userDoc snapshot — supersedes a previously queued one.
  cancelPendingAdvancedMatching();

  if (isDoNotSellSignalActive()) {
    logGpcSuppressionOnce();
    return;
  }
  const amParams = toMetaAdvancedMatchingParams(dlUserData);
  if (!amParams) return;

  // Graceful no-op (not a throw) when the env var isn't configured — expected
  // locally/on preview deploys without it, never expected in production.
  const pixelId = META_PIXEL_ID;
  if (!pixelId) {
    warnMissingPixelIdOnce();
    return;
  }

  const fbq = getFbq();
  if (fbq) {
    if (isPixelIdDrifted(fbq, pixelId)) return;
    // Codex P2 (final review round): the init transport can throw where the
    // old rejected 'set' call never did (duplicate-init edge, ad-blocker
    // shims). AuthContext's userDoc snapshot calls this helper unwrapped —
    // identity attachment must never break the caller.
    try {
      fbq('init', pixelId, amParams);
      identityAttachedThisPage = true;
    } catch (initError) {
      logError(initError);
    }
    return;
  }

  // No pixel yet, and no window means no browser timers to wait in (SSR —
  // fbq will never appear there anyway).
  if (typeof window === 'undefined') return;

  pendingAmParams = amParams;
  pendingAmDeadline = Date.now() + AM_RETRY_TIMEOUT_MS;
  pendingAmTimer = setInterval(() => {
    if (Date.now() > pendingAmDeadline) {
      cancelPendingAdvancedMatching();
      return;
    }
    const retryFbq = getFbq();
    if (!retryFbq) return;
    // Re-check GPC/opt-out: state may have changed while this was pending
    // (e.g. the user opted out in the gap between snapshot and pixel load).
    if (isDoNotSellSignalActive()) {
      logGpcSuppressionOnce();
      cancelPendingAdvancedMatching();
      return;
    }
    const paramsToSend = pendingAmParams;
    cancelPendingAdvancedMatching();
    if (paramsToSend) {
      if (isPixelIdDrifted(retryFbq, pixelId)) return;
      try {
        retryFbq('init', pixelId, paramsToSend);
        identityAttachedThisPage = true;
      } catch (initError) {
        logError(initError);
      }
    }
  }, AM_RETRY_INTERVAL_MS);
}

/**
 * Cancels any still-pending Advanced Matching retry (see the pendingAmParams
 * doc above setMetaAdvancedMatching) so a queued identity set can never fire
 * AFTER a logout, opt-out, or guest conversion has already run.
 *
 * DOES NOT — and as of this fix, CANNOT — clear pixel state. Earlier
 * versions of this function called fbq('init', PIXEL_ID, {}) and then
 * fbq('init', PIXEL_ID, EMPTY_AM_PARAMS) (adversarial fix C1/C2), each
 * believed to be a stronger clear than the last. Both were WRONG, proven by
 * direct wire observation, not merely undocumented-and-assumed:
 *
 * Phase A-E wire experiments, live on gearfocus.com, 2026-07-15 (full
 * evidence table in PR #1436; see also memory note
 * reference_meta_pixel_first_write_wins): Meta's browser pixel is
 * FIRST-WRITE-WINS. Once an init call sets em/ph/fn/etc. on a field, NO
 * subsequent init call in the same page life overwrites that field —
 * confirmed against empty strings (Phase C), a completely different email
 * (Phase D), and random garbage (Phase E). All three were silently ignored;
 * the field visible to Meta stayed whatever the FIRST successful init call
 * set it to. There is no in-page mechanism, of any shape, that clears
 * previously-attached Advanced Matching identity.
 *
 * DO NOT re-add an init-based clear here. It has been tested and does not
 * work — this is not a documentation gap to research further, it is a wire-
 * observed fact about Meta's pixel implementation.
 *
 * The only real clear is a hard navigation: a fresh page load re-runs
 * fbevents.js from scratch with no identity attached. Callers that need a
 * guaranteed-clean pixel now do that navigation themselves — see
 * AuthContext.logout() (always hard-navigates home), CheckoutProcessV3.tsx /
 * ExpressCheckoutButton.tsx (guest purchase confirmation hard-navigates when
 * isPixelIdentityAttached() is true), and the cross-tab opt-out listener
 * below (reloads the tab, same gate). This function's only remaining job is
 * the pending-retry cancellation described above.
 */
export function clearMetaAdvancedMatching(): void {
  cancelPendingAdvancedMatching();
}

/**
 * Cross-tab opt-out sync (adversarial review CPO M1). Advanced Matching
 * identity is per-tab pixel memory. If a user opts out via the Privacy
 * Policy toggle in tab A, tab B's pixel still carries whatever identity was
 * attached before the opt-out — a localStorage write never fires a
 * 'storage' event in the tab that made it (only in OTHER same-origin tabs,
 * per the Storage API spec), and PrivacyPolicyPage.tsx's own opt-out handler
 * only hard-navigates the tab that clicked Opt Out (see PrivacyPolicyPage.tsx).
 * This listener is what clears every OTHER open tab.
 *
 * Phase A-E wire evidence (see clearMetaAdvancedMatching's doc comment
 * above): there is no in-page clear, so "clears" here means "reloads" — a
 * hard navigation is the only real option. Gated on isPixelIdentityAttached()
 * so this doesn't force a disruptive, silent reload on every open tab —
 * most tabs never attach identity at all (GPC already active, no pixel id,
 * anonymous browsing with no purchase/login yet), and reloading those for
 * no reason would be a pure UX regression with zero privacy benefit. Loop-
 * safe: reloading doesn't write to gf_do_not_sell again, so it can't
 * re-trigger this listener, and the reloaded page immediately sees
 * gf_do_not_sell=true via isDoNotSellSignalActive() and never attaches
 * identity again.
 *
 * Registered once at module load, guarded for SSR (no window on the server).
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === 'gf_do_not_sell' && e.newValue === 'true') {
      // Codex P2 fix (GB #1373): a broken/hostile browser API (reload
      // throwing in an exotic embedded/sandboxed context) must never break
      // this listener — an uncaught throw here would surface as an
      // unhandled error on the 'storage' event dispatch, which has no
      // caller to catch it.
      try {
        clearMetaAdvancedMatching();
        if (isPixelIdentityAttached()) {
          window.location.reload();
        }
      } catch (clearError) {
        logError(clearError);
      }
    }
  });
}

export const trackEvent = (
  eventType: EcommerceEventType,
  eventData: Record<string, any>,
  userData?: Record<string, string> | null
) => {
  if (!canPush) return;
  if (eventType === 'purchase') return; // hard guard, independent of canPush
  setTimeout(() => {
    pushToDataLayer(eventType, {
      // Top-level event_id for GTM → Stape CAPI dedup (must match fbq eventID)
      ...(eventData.event_id ? { event_id: eventData.event_id } : {}),
      // Top-level shipping_type for GTM DLV → GA4 custom dimension (#1105)
      ...(eventData.shipping_type ? { shipping_type: eventData.shipping_type } : {}),
      ecommerce: { ...eventData },
      ...(userData || {}),
    });
  }, 0);
};

/**
 * Diagnostic (non-ecommerce) event push for cart-health telemetry (TASK-1323).
 *
 * Reuses the EXACT same `canPush` gate as `trackEvent` — so these events reach
 * `window.dataLayer` on Vercel previews (per #1410/#1413) and are inspectable in
 * GA4 DebugView on staging — but deliberately does NOT wrap the payload in an
 * `ecommerce` object. These are engineering-health signals (drawer-empty
 * mismatch, focus-clobber, snapshot errors, add failures), NOT funnel events;
 * routing them through `trackEvent` would pollute GA4's ecommerce reports and
 * require widening the closed `EcommerceEventType` union with non-commerce names.
 *
 * Purchase has no special-casing here (these are never purchase events), and
 * the same GPC/opt-out flag handling in `pushToDataLayer` still applies.
 */
export const trackCartHealthEvent = (
  eventName: string,
  payload: Record<string, unknown>
) => {
  if (!canPush) return;
  setTimeout(() => {
    pushToDataLayer(eventName, payload);
  }, 0);
};

export const formatProductItems = (
  products: ProductDocument[],
  listId?: string
) =>
  products.map((product) => ({
    // CHANGED: Use Firestore doc ID (not SKU) for Meta catalog matching
    item_id: product.id,
    item_name: product.title,
    price: product.price,
    currency: 'USD',
    discount:
      product.on_sale && !product.out_of_stock
        ? Math.round((product.og_price ?? product.price) - product.price)
        : undefined,
    item_brand: product.brand,
    ...(product.categories?.reduce<Record<string, any>>(
      (acc, category, index) => {
        acc[`item_category${index > 0 ? index : ''}`] = category;
        return acc;
      },
      {}
    ) || {}),
    ...(listId ? { item_list_id: listId, item_list_name: listId } : {}),
  }));

export const getShippingType = (items: CartItem[]): string => {
  if (!items.length) return 'unknown';
  const allFlat = items.every((i) => i.is_flat_rate);
  const allCarrier = items.every((i) => !i.is_flat_rate);
  return allFlat ? 'all_flat_rate' : allCarrier ? 'all_carrier' : 'mixed';
};

export const formatCartItems = (items: CartItem[]) =>
  items.map((item) => ({
    // CHANGED: Use Firestore doc ID (not SKU) for Meta catalog matching
    item_id: item.product_id,
    item_name: item.product_title,
    price: item.product_cost,
    currency: 'USD',
    item_brand: item.product_brand,
    ...(item.product_categories?.reduce<Record<string, any>>(
      (acc, category, index) => {
        acc[`item_category${index > 0 ? index : ''}`] = category;
        return acc;
      },
      {}
    ) || {}),
    quantity: item.qty || 1,
  }));
